<template>
  <div
    v-if="isContentReady"
    class="ovp-a11y-container"
    :app-language="selectedLanguage"
    :app-name="appName$"
    :app-version="version$"
  >
    <a-b-config />
    <auth-modal />
    <the-confirmation />
    <the-loading />
    <div id="modals"></div>
    <medical-office-search />
    <product-replace-modal />
    <the-snackbar />
    <save-nudge v-if="isNudgingEnabled" />
  </div>
</template>

<script setup>
  import { computed, inject, onBeforeMount, onMounted, watch } from 'vue'

  import { events$, loading$ } from '@shared/services'

  import masterDataAPI from '@shared/api/masterData'
  import basketStore from '@shared/store/basket'
  import partnerStore from '@shared/store/partner'
  import useApplication from '@shared/hooks/useApplication'
  import useI18n from '@shared/hooks/useI18n'
  import usePerson from '@shared/hooks/usePerson'
  import useSession from '@shared/hooks/useSession'
  import useSync from '@shared/hooks/useSync'
  import useUrl from '@shared/hooks/useUrl'

  import ABConfig from '@shared/components/Container/ABConfig'
  import ProductReplaceModal from '@shared/components/Product/Replace/ReplaceModal'
  import MedicalOfficeSearch from '@shared/components/MedicalSearch/MedicalOfficeSearch'
  import AuthModal from '@shared/components/Authentication/AuthModal'
  import TheConfirmation from '@shared/components/Modal/Confirmation'
  import TheLoading from '@shared/components/Container/Loading'
  import TheSnackbar from '@shared/components/Basic/Snackbar'
  import SaveNudge from '@shared/components/Basket/SaveNudge/SaveNudge'

  import BasketWidget from '@shared/modules/WebWidgets/components/BasketWidget'
  import EntryWidget from '@shared/modules/WebWidgets/components/EntryWidget'
  import FabWidget from '@shared/modules/WebWidgets/components/FabWidget'
  import ProductWidget from '@shared/modules/WebWidgets/components/ProductWidget/ProductWidget'
  import { registerContinuously, registerOnce } from '@shared/modules/WebWidgets/Registration'

  import { registerBasketActionListeners, registerUpdateBasketListeners } from '@shared/helpers/GlobalEventHandler'

  import { EVENT_TRACKING, EVENT_URL } from '@shared/config/events'
  import {
    CHANNEL,
    DEFAULT_DISTRIBUTION_PARTNER_NUMBER,
    PACKAGE,
    WIDGET_SELECTOR,
  } from '@shared/config/constants'

  import '@shared/assets/css/import.css'

  // HOOKS
  const { changeLanguage, getDvpUrl, initializeClearSequence, isContentReady, isReadyWithPartner } = useApplication()
  const { selectedLanguage } = useI18n()
  const { createPerson } = usePerson()
  const { autostart } = useSession()
  const { registerSync } = useSync()
  const { getParamFromUrl, react, sanitizePrefillParams } = useUrl()

  // INIT
  const appName$ = inject('appName$')
  const version$ = inject('version$')

  // COMPUTED
  const isNudgingEnabled = computed(() => {
    return basketStore.basket.persons?.length > 0 && !basketStore.basket.existingCustomer
  })

  // METHODS
  function emitEventWhenReady() {
    return new Promise(function (resolve) {
      if (isReadyWithPartner.value) resolve()

      const unwatch = watch(
        () => isReadyWithPartner.value,
        () => {
          unwatch()
          resolve()
        }
      )
    })
  }

  async function prefillUser() {
    const contractStartDate = basketStore.basket.contractStartDate

    if (getParamFromUrl('open') === 'prefill' && getParamFromUrl('data')) {
      try {
        loading$.start()

        const prefillData = sanitizePrefillParams(getParamFromUrl('data'))

        const locations = await masterDataAPI.getLocations({
          query: prefillData.zipCode,
          referenceDate: contractStartDate,
        })
        const location = locations.find(l => {
          return (
            l.primeRegion === prefillData.primeRegion &&
            l.locationNumber === prefillData.locationNumber &&
            l.zipCode === prefillData.zipCode &&
            l.zipCodeAddon === prefillData.zipCodeAddon
          )
        })

        if (!location) throw new Error()

        const formData = {
          contractStartDate,
          address: location,
          persons: [
            await createPerson({
              dateOfBirth: prefillData.dateOfBirth,
              firstName: prefillData.firstName,
              gender: prefillData.gender,
            }),
          ],
        }

        await basketStore.updateOvpBasket(formData)
      } catch {
      // noop
      }

      loading$.end()
    }
  }

  function registerWidgets() {
    registerContinuously(BasketWidget, WIDGET_SELECTOR.BASKET)
    registerContinuously(EntryWidget, WIDGET_SELECTOR.ENTRY, props => props)
    registerOnce(FabWidget, WIDGET_SELECTOR.FAB)
    registerContinuously(ProductWidget, WIDGET_SELECTOR.PRODUCT)
  }

  // LIFECYCLE HOOKS
  onBeforeMount(async () => {
    initializeClearSequence()
    const __basket = await autostart()
    const additionalApps = [PACKAGE.PRODUCTS, PACKAGE.PRODUCT_WIDGET]
    await changeLanguage({ additionalApps })

    if (basketStore.isTemporaryBasket()) {
      await partnerStore.install({
        contractStartDate: __basket.contractStartDate,
        language: __basket.language,
        collectiveNumber: null,
        partnerNumber: null,
        distributionPartnerNumber: DEFAULT_DISTRIBUTION_PARTNER_NUMBER,
      })
    }

    registerWidgets()
    prefillUser()
  })

  onMounted(() => {
    registerUpdateBasketListeners()
    registerBasketActionListeners()

    emitEventWhenReady().then(() => {
      // tab-sync needs to be registered, after all the BE-/localStorage operations are done
      registerSync()

      // react to URL-params (e.g. open login)
      react()
    })

    events$.on(EVENT_URL.GOTO, params => {
      // @note: entered should only be triggered, if entry happens from fresh productWidget
      if (basketStore.basket.channel === CHANNEL.PRODUCTWIDGET) {
        events$.emit(EVENT_TRACKING.ENTERED, { source: CHANNEL.PRODUCTWIDGET })
      }

      const target = params?.route?.path?.split('/')[0]
      window.location.href = getDvpUrl(selectedLanguage.value) + '/' + target
    })

    document.addEventListener('keydown', event => {
      if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Tab'].includes(event.code)) {
        window.setTimeout(() => {
          if (document.activeElement?.classList && document.activeElement.classList.contains('outline')) {
            document.activeElement.classList.add('has-outline')
          }
        })
      }
    })
  })
</script>

<style name="global">
:root {
  /* the values have to be 5 < x < 99 accoring to dotcom */
  --dvp-stack-level-element: 11;
  --dvp-stack-level-popup-menu: 12;
  --dvp-stack-level-modal: 13;
  --dvp-stack-level-modal-stacked: 14; /* exception for medical-office-search modal as it can be opened within another modal */
  --dvp-stack-level-popup-notification: 15;
  --dvp-stack-level-tooltip: 1000; /* needs to be higher than dotcom-navigation (z-index: 999) */
  --dvp-stack-level-system-notification: 17;
  --dvp-stack-level-screen-blocker: 18;
}

#basketWidget,
.productWidget,
#fab-widget,
.sanitasOvpEntry,
.dvp-modal-container,
.dropdown[role='listbox'] {
  font-family: var(--font-family-open-sans);
  font-style: var(--font-style-default);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-regular-lg);
  letter-spacing: var(--letter-spacing-regular-lg);
  color: var(--Text-Primary);
}

#basketWidget *:focus,
.productWidget *:focus,
#fab-widget *:focus,
.sanitasOvpEntry *:focus,
.dvp-modal-container *:focus,
.dropdown[role='listbox'] *:focus {
  outline: none;
}

#basketWidget *,
#basketWidget *:after,
#basketWidget *:before,
.productWidget *,
.productWidget *:after,
.productWidget *:before,
#fab-widget *,
#fab-widget *:after,
#fab-widget *:before,
.sanitasOvpEntry *,
.sanitasOvpEntry *::after,
.sanitasOvpEntry *:before,
.dvp-modal-container *,
.dvp-modal-container *:after,
.dvp-modal-container *:before,
.dropdown[role='listbox'] *,
.dropdown[role='listbox'] *:after,
.dropdown[role='listbox'] *:before {
  box-sizing: border-box;
}

.tippy-box[data-theme~='dvp'] {
  min-width: 144px;
  padding: var(--fixed-spacing-fix-06);
  background-color: var(--surface);
  border-radius: var(--fixed-border-radius-fix-02);
  box-shadow: var(--elevation-level-5);

  .tippy-content {
    font-weight: var(--font-weight-regular);
    font-size: var(--font-size-regular-sm);
    letter-spacing: var(--letter-spacing-regular-sm);
    color: var(--on-surface);
    padding: 0;
  }

  .tippy-arrow {
    color: var(--surface);

    &::before {
      transform: scale(1.25);
    }
  }
}

.tippy-box[data-theme~='card'] {
  background-color: var(--surface);
  border-radius: var(--fixed-border-radius-fix-02);
  box-shadow: var(--elevation-level-5);
  padding: var(--fixed-spacing-fix-02);
  margin: 0;
  & > .tippy-content {
    padding: 0;
    margin: 0;
    height: max-content;

    & > .card__image {
      margin: 0;
      padding: 0;
      width: 100%;
    }
  }

  .tippy-arrow {
    color: var(--surface);

    &::before {
      transform: scale(1.25);
    }
  }
}

.tippy-box[data-placement='top-end'] > {
  .tippy-arrow {
    right: 20px !important;
    left: initial !important;
  }
}
</style>
